import { useEffect, useRef, useState } from "react";

const MIN_INTERVAL_MS = 1200;
const MAX_INTERVAL_MS = 5000;

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i]!;
    a[i] = a[j]!;
    a[j] = tmp;
  }
  return a;
}

function clampInterval(ms: number) {
  return Math.min(MAX_INTERVAL_MS, Math.max(MIN_INTERVAL_MS, ms));
}

/**
 * Cycles through `words` while `active` is true.
 *
 * Upload mode  – pass `estimatedDurationMs`; interval = duration / words.length
 * Processing mode – pass `progress` (0-100); interval is recalculated after
 *   every progress tick using elapsed time so the words spread evenly across
 *   the remaining duration rather than bunching up or repeating.
 */
export function useCyclingWord(
  words: string[],
  active: boolean,
  opts: { estimatedDurationMs?: number; progress?: number } = {}
): string {
  const { estimatedDurationMs, progress } = opts;

  // Shuffle once per activation so repeat cycles look different
  const [shuffled, setShuffled] = useState<string[]>(() => shuffle(words));
  const [index, setIndex] = useState(0);

  const indexRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastProgressRef = useRef<number | undefined>(undefined);

  // Reschedule the next word swap — called after every tick so interval can adapt
  const schedule = useRef<(wordsLen: number) => void>(() => {});

  useEffect(() => {
    schedule.current = (wordsLen: number) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      let interval: number;

      if (progress !== undefined && progress > 0) {
        // Processing mode: use elapsed/progress to estimate remaining time
        const elapsed = Date.now() - startTimeRef.current;
        const msPerPercent = elapsed / progress;
        const remaining = msPerPercent * (100 - progress);
        const wordsLeft = wordsLen - indexRef.current;
        interval = clampInterval(remaining / Math.max(wordsLeft, 1));
      } else if (estimatedDurationMs !== undefined) {
        interval = clampInterval(estimatedDurationMs / wordsLen);
      } else {
        interval = 2000;
      }

      timerRef.current = setTimeout(() => {
        indexRef.current = (indexRef.current + 1) % wordsLen;
        setIndex(indexRef.current);
        schedule.current(wordsLen);
      }, interval);
    };
  });

  useEffect(() => {
    if (!active) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Reset on activation
    const words = shuffle(shuffled);
    setShuffled(words);
    indexRef.current = 0;
    setIndex(0);
    startTimeRef.current = Date.now();
    lastProgressRef.current = undefined;
    schedule.current(words.length);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // When progress ticks, reschedule immediately with updated estimate
  useEffect(() => {
    if (!active || progress === undefined) return;
    if (progress === lastProgressRef.current) return;
    lastProgressRef.current = progress;
    schedule.current(shuffled.length);
  }, [active, progress, shuffled.length]);

  return shuffled[index] ?? words[0] ?? "";
}
